import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { CheckSquare, Plus, Circle, CheckCircle2, X, Trash2, Edit2, Flag } from 'lucide-react';
import { toast } from 'sonner';

const Tasks = ({ user }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'open',
    due_date: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch(getBackendUrl() + '/api/tasks', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Fehler beim Laden der Aufgaben');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingTask 
        ? `${getBackendUrl()}/api/tasks/${editingTask._id}`
        : `${getBackendUrl()}/api/tasks`;
      
      const method = editingTask ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingTask ? 'Aufgabe aktualisiert' : 'Aufgabe erstellt');
        setShowModal(false);
        setEditingTask(null);
        setFormData({ title: '', description: '', priority: 'medium', status: 'open', due_date: '' });
        fetchTasks();
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Möchten Sie diese Aufgabe wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Aufgabe gelöscht');
        fetchTasks();
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/tasks/${task._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...task, status: newStatus })
      });

      if (response.ok) {
        toast.success('Status aktualisiert');
        fetchTasks();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      status: task.status,
      due_date: task.due_date || ''
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingTask(null);
    setFormData({ title: '', description: '', priority: 'medium', status: 'open', due_date: '' });
    setShowModal(true);
  };

  const stats = {
    open: tasks.filter(t => t.status === 'open').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-orange-600 bg-orange-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'in_progress': return <Circle className="w-5 h-5 text-blue-600" />;
      default: return <Circle className="w-5 h-5 text-orange-600" />;
    }
  };

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Aufgaben</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1">Verwalten Sie Ihre Aufgaben und To-Dos</p>
          </div>
          <button
            onClick={openCreateModal}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-all min-h-[44px]"
            style={{backgroundColor: '#0A4D8C'}}
            data-testid="add-task-button"
          >
            <Plus className="w-4 h-4" />
            Neue Aufgabe
          </button>
        </div>

        {/* Task Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#FEF3C7'}}>
                <Circle className="w-5 h-5 md:w-6 md:h-6" style={{color: '#D97706'}} />
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Offen</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{stats.open}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#DBEAFE'}}>
                <Circle className="w-5 h-5 md:w-6 md:h-6" style={{color: '#2563EB'}} />
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">In Arbeit</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{stats.in_progress}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#D1FAE5'}}>
                <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6" style={{color: '#059669'}} />
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Erledigt</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{stats.completed}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
          <div className="p-6 border-b" style={{borderColor: '#E1E4E8'}}>
            <h3 className="text-lg font-semibold text-gray-900">Alle Aufgaben</h3>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 mx-auto" style={{borderTopColor: '#0A4D8C'}}></div>
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Keine Aufgaben vorhanden</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div
                    key={task._id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    style={{borderColor: '#E1E4E8'}}
                    data-testid={`task-${task._id}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => {
                          const newStatus = task.status === 'completed' ? 'open' : 'completed';
                          handleStatusChange(task, newStatus);
                        }}
                        className="hover:scale-110 transition-transform"
                      >
                        {getStatusIcon(task.status)}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                            {task.title}
                          </h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                            {task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                          </span>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                        )}
                        {task.due_date && (
                          <p className="text-xs text-gray-400 mt-1">
                            Fällig: {new Date(task.due_date).toLocaleDateString('de-DE')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(task)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(task._id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b" style={{borderColor: '#E1E4E8'}}>
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingTask ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    style={{borderColor: '#E1E4E8'}}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    style={{borderColor: '#E1E4E8'}}
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priorität</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{borderColor: '#E1E4E8'}}
                    >
                      <option value="low">Niedrig</option>
                      <option value="medium">Mittel</option>
                      <option value="high">Hoch</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{borderColor: '#E1E4E8'}}
                    >
                      <option value="open">Offen</option>
                      <option value="in_progress">In Arbeit</option>
                      <option value="completed">Erledigt</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fälligkeitsdatum</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    style={{borderColor: '#E1E4E8'}}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50"
                    style={{borderColor: '#E1E4E8'}}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 text-white rounded-lg"
                    style={{backgroundColor: '#0A4D8C'}}
                  >
                    {editingTask ? 'Aktualisieren' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Tasks;